import React from 'react';
import { useTranslation } from 'react-i18next';

import { Icon } from '../../../../components';
import { normaliseKeywords } from '../../seo-document';
import { KEYWORD_MAX, KEYWORDS_MAX } from '../../seo-types';
import {
  Root,
  Label,
  ChipsRow,
  Chips,
  Chip,
  ChipLabel,
  FocusMark,
  RemoveButton,
  Input,
  Hint,
} from './KeywordsField.styles';

export type KeywordsFieldProps = {
  label: string;
  value: readonly string[];
  onChange: (next: string[]) => void;
  placeholder?: string;
  disabled?: boolean;
  /** Shown muted, with no remove button, while `value` is empty (keywords the route inherits). */
  inherited?: readonly string[];
};

/**
 * A keyword list as chips: the first is the focus keyword, the rest are secondary. Typing then Enter, a comma or
 * leaving the input adds a keyword; × or Backspace on an empty input removes one. Every change goes through the
 * server's normalisation, so the list never holds a duplicate, a blank or more than {@link KEYWORDS_MAX}.
 */
export const KeywordsField = ({
  label,
  value,
  onChange,
  placeholder,
  disabled = false,
  inherited = [],
}: KeywordsFieldProps): React.ReactElement => {
  const { t } = useTranslation();
  const [typed, setTyped] = React.useState('');
  const full = value.length >= KEYWORDS_MAX;
  const showInherited = value.length === 0 && inherited.length > 0;
  const chips = showInherited ? inherited : value;

  const add = (entries: readonly string[]): void => {
    const next = normaliseKeywords([...value, ...entries]);
    if (next.length !== value.length || next.some((keyword, i) => keyword !== value[i])) onChange(next);
  };

  const remove = (index: number): void => onChange(value.filter((_, i) => i !== index));

  const onInput = (event: React.ChangeEvent<HTMLInputElement>): void => {
    const parts = event.target.value.split(',');
    // A comma (typed or pasted) closes every keyword before it; the text after the last one keeps being typed.
    const rest = parts.pop() ?? '';
    if (parts.length) add(parts);
    setTyped(rest);
  };

  const commit = (): void => {
    if (typed.trim() !== '') add([typed]);
    setTyped('');
  };

  const onKeyDown = (event: React.KeyboardEvent<HTMLInputElement>): void => {
    if (event.key === 'Enter') {
      event.preventDefault();
      commit();
    } else if (event.key === 'Backspace' && typed === '' && value.length > 0) {
      event.preventDefault();
      remove(value.length - 1);
    }
  };

  return (
    <Root $disabled={disabled}>
      <Label text={label} tag="span" />
      <ChipsRow>
        {chips.length ? (
          <Chips>
            {chips.map((keyword, index) => (
              <Chip
                key={keyword}
                $inherited={showInherited}
                data-testid="keyword-chip"
                data-inherited={showInherited ? 'true' : 'false'}
                aria-label={index === 0 ? t('Focus keyword: {{keyword}}', { keyword }) : undefined}
              >
                {index === 0 ? <FocusMark text="Focus" tag="span" /> : null}
                <ChipLabel text={keyword} tag="span" raw />
                {showInherited ? null : (
                  <RemoveButton
                    type="button"
                    aria-label={t('Remove {{keyword}}', { keyword })}
                    disabled={disabled}
                    onClick={() => remove(index)}
                  >
                    <Icon name="close" size={14} />
                  </RemoveButton>
                )}
              </Chip>
            ))}
          </Chips>
        ) : null}
        <Input
          type="text"
          value={typed}
          aria-label={t(label)}
          placeholder={placeholder && !full ? t(placeholder) : undefined}
          disabled={disabled || full}
          maxLength={KEYWORD_MAX}
          onChange={onInput}
          onKeyDown={onKeyDown}
          onBlur={commit}
        />
      </ChipsRow>
      {full ? <Hint text="Up to 5 keywords: one focus keyword and four secondary ones" tag="span" /> : null}
    </Root>
  );
};

export default KeywordsField;
